import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarCheck,
  Camera,
  Check,
  ChevronRight,
  Image as ImageIcon,
  Mail,
  MapPin,
  Menu,
  Palette,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Wand2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Seo } from "@/components/seo/Seo";

type DemoStyle = {
  slug: string;
  name: string;
  variant: "local" | "ops" | "premium";
  company: string;
  headline: string;
  subheadline: string;
  primary: string;
  secondary: string;
  surface: string;
  heroClass: string;
  tone: string;
  fontFamily: string;
  headingFontFamily: string;
  heroImage: string;
  serviceImages: string[];
};

const demoStyles: DemoStyle[] = [
  {
    slug: "chiaro-locale",
    name: "Chiaro locale",
    variant: "local",
    company: "Laguna Clean",
    headline: "Un sito locale che trasforma visite in richieste di pulizia.",
    subheadline: "Ideale per imprese familiari che vogliono una presenza online semplice, credibile e collegata a Halomora.",
    primary: "#0284c7",
    secondary: "#06b6d4",
    surface: "#f0f9ff",
    heroClass: "bg-gradient-to-br from-sky-50 via-white to-cyan-50 text-slate-950",
    tone: "Pulito, familiare, locale.",
    fontFamily: "'Trebuchet MS', Arial, sans-serif",
    headingFontFamily: "Georgia, 'Times New Roman', serif",
    heroImage: "/assets/public-photos/optimized/pexels-matilda-wormwood-4097993.jpg",
    serviceImages: [
      "/assets/public-photos/optimized/pexels-tima-miroshnichenko-6195274.jpg",
      "/assets/public-photos/optimized/pexels-cottonbro-6466212.jpg",
      "/assets/public-photos/optimized/pexels-liliana-drew-9462733.jpg",
    ],
  },
  {
    slug: "operativo-diretto",
    name: "Operativo diretto",
    variant: "ops",
    company: "Nord Est Pulizie",
    headline: "Un sito operativo per ricevere richieste ordinate e gestibili.",
    subheadline: "Pensato per team strutturati che vogliono un sito professionale collegato a preventivi, zone e backend.",
    primary: "#059669",
    secondary: "#14b8a6",
    surface: "#ecfdf5",
    heroClass: "bg-gradient-to-br from-emerald-950 via-slate-950 to-teal-950 text-white",
    tone: "Solido, pratico, orientato al lavoro.",
    fontFamily: "Arial, Helvetica, sans-serif",
    headingFontFamily: "'Arial Narrow', Arial, Helvetica, sans-serif",
    heroImage: "/assets/public-photos/optimized/pexels-tima-miroshnichenko-6196677.jpg",
    serviceImages: [
      "/assets/public-photos/optimized/pexels-tima-miroshnichenko-6197108.jpg",
      "/assets/public-photos/optimized/pexels-tima-miroshnichenko-6195278.jpg",
      "/assets/public-photos/optimized/pexels-cottonbro-6502300.jpg",
    ],
  },
  {
    slug: "premium-hospitality",
    name: "Premium hospitality",
    variant: "premium",
    company: "Mare Suite Service",
    headline: "Un sito premium per vendere servizi di turnover e hospitality.",
    subheadline: "Per imprese che lavorano con appartamenti vacanze, property manager e strutture ricettive, con booking integrato.",
    primary: "#7c3aed",
    secondary: "#d946ef",
    surface: "#f5f3ff",
    heroClass: "bg-gradient-to-br from-violet-950 via-slate-950 to-fuchsia-950 text-white",
    tone: "Elegante, hospitality, premium.",
    fontFamily: "'Optima', 'Segoe UI', Arial, sans-serif",
    headingFontFamily: "Georgia, 'Times New Roman', serif",
    heroImage: "/assets/public-photos/optimized/pexels-olly-3772615.jpg",
    serviceImages: [
      "/assets/public-photos/optimized/pexels-cottonbro-6466490.jpg",
      "/assets/public-photos/optimized/pexels-cottonbro-6466216.jpg",
      "/assets/public-photos/optimized/pexels-cottonbro-6466212.jpg",
    ],
  },
];

const navItems = [
  { page: "home", label: "Home" },
  { page: "servizi", label: "Servizi" },
  { page: "zone", label: "Zone" },
  { page: "preventivo", label: "Preventivo" },
  { page: "faq", label: "FAQ" },
];

const services = [
  ["Pulizia di cambio", "Tra check-out e check-in, con controllo finale prima dell'arrivo degli ospiti."],
  ["Pulizia di fondo", "Intervento approfondito per inizio stagione, post lavori o ripartenze importanti."],
  ["Uffici e attivita", "Pulizie ricorrenti per studi, negozi, uffici e spazi commerciali."],
  ["Post-cantiere", "Servizi straordinari quando serve rimettere in ordine ambienti appena rinnovati."],
  ["Sanificazione", "Trattamenti professionali per ambienti ad alta frequentazione."],
  ["Manutenzione leggera", "Piccoli interventi coordinati con il servizio di pulizia."],
];

const zones = ["Jesolo", "Cavallino-Treporti", "Caorle", "Venezia", "Bibione", "Eraclea"];

export default function BusinessBookingSiteDemo() {
  const navigate = useNavigate();
  const { style, page } = useParams();
  const selectedStyle = demoStyles.find((item) => item.slug === style) || demoStyles[0];
  const activePage = page || "home";
  const [companyName, setCompanyName] = useState(selectedStyle.company);
  const [primaryColor, setPrimaryColor] = useState(selectedStyle.primary);
  const [secondaryColor, setSecondaryColor] = useState(selectedStyle.secondary);
  const [showBrandTip, setShowBrandTip] = useState(true);
  const [showColorTip, setShowColorTip] = useState(true);
  const [showPageTip, setShowPageTip] = useState(true);
  const [miniCustomizerOpen, setMiniCustomizerOpen] = useState(false);
  const [miniCustomizerDismissed, setMiniCustomizerDismissed] = useState(false);
  const demoBasePath = `/for-businesses/booking-system-demo/${selectedStyle.slug}`;
  const pagePath = (targetPage: string) => (targetPage === "home" ? demoBasePath : `${demoBasePath}/${targetPage}`);
  const activeNavLabel = navItems.find((item) => item.page === activePage)?.label || "Home";

  const cssVars = useMemo(
    () =>
      ({
        "--demo-primary": primaryColor,
        "--demo-secondary": secondaryColor,
        "--demo-surface": selectedStyle.surface,
        "--demo-font": selectedStyle.fontFamily,
        "--demo-heading-font": selectedStyle.headingFontFamily,
      }) as CSSProperties,
    [primaryColor, secondaryColor, selectedStyle.fontFamily, selectedStyle.headingFontFamily, selectedStyle.surface],
  );

  const setStylePreset = (preset: DemoStyle) => {
    navigate(`/for-businesses/booking-system-demo/${preset.slug}${activePage === "home" ? "" : `/${activePage}`}`);
    setCompanyName(preset.company);
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
  };

  return (
    <div className="min-h-screen bg-white" style={{ ...cssVars, fontFamily: "var(--demo-font)" }}>
      <Seo
        title={`${selectedStyle.name} | Demo sito impresa pulizie Halomora`}
        description="Anteprima interattiva di un sito brandizzato per impresa di pulizie, con servizi, zone, FAQ e form preventivo collegati a Halomora."
        canonicalPath="/for-businesses/booking-system-demo"
        noindex
      />

      <div className="sticky top-16 z-40 border-b border-slate-200/80 bg-white/95 shadow-sm">
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3">
          <button
            type="button"
            onClick={() => navigate("/for-businesses/booking-system-demo")}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-950"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alle demo
          </button>
          <div className="hidden items-center gap-2 md:flex">
            {demoStyles.map((preset) => (
              <button
                key={preset.slug}
                type="button"
                onClick={() => setStylePreset(preset)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  preset.slug === selectedStyle.slug
                    ? "border-slate-950 bg-slate-950 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className={activePage === "home" ? "border-b border-slate-200 bg-slate-50" : "hidden"}>
        <div className="container mx-auto grid max-w-7xl gap-5 px-6 py-5 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="relative rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="absolute -top-3 left-5 hidden rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-white shadow-lg md:block">
              Prova ora: crea una prima bozza del tuo sito
            </div>
            <div className="mb-4 flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
                  <Wand2 className="h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                  Personalizza in 20 secondi
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Cambia nome e colori: la demo si aggiorna subito, come primo assaggio del tuo sito brandizzato.
                </p>
              </div>
              <Badge className="w-fit bg-primary/10 text-primary hover:bg-primary/10">Demo interattiva</Badge>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="relative">
                <Input value={companyName} onChange={(event) => setCompanyName(event.target.value)} placeholder="Nome azienda" />
                {showBrandTip ? (
                  <div className="absolute left-0 top-[calc(100%+0.5rem)] z-20 w-72 rounded-2xl border border-sky-100 bg-sky-50 p-4 text-xs text-sky-950 shadow-md">
                    <div className="font-bold">Prova con il tuo nome</div>
                    <p className="mt-1 leading-relaxed">
                      Scrivi il nome della tua impresa: lo vedrai comparire subito nel logo e nella pagina.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowBrandTip(false)}
                      className="mt-3 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-bold text-white"
                    >
                      Ok, provo
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="relative md:col-span-2 grid gap-3 md:grid-cols-2">
                <label className="flex h-12 items-center gap-3 rounded-[1rem] border border-slate-200 bg-white px-4 text-sm text-slate-700">
                  Colore principale
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(event) => setPrimaryColor(event.target.value)}
                    className="ml-auto h-7 w-10 cursor-pointer rounded border-0 bg-transparent"
                  />
                </label>
                <label className="flex h-12 items-center gap-3 rounded-[1rem] border border-slate-200 bg-white px-4 text-sm text-slate-700">
                  Colore secondario
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(event) => setSecondaryColor(event.target.value)}
                    className="ml-auto h-7 w-10 cursor-pointer rounded border-0 bg-transparent"
                  />
                </label>
                {showColorTip ? (
                  <div className="absolute right-0 top-[calc(100%+0.5rem)] z-20 w-80 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs text-emerald-950 shadow-md">
                    <div className="font-bold">Dai personalita al sito</div>
                    <p className="mt-1 leading-relaxed">
                      Cambia i colori per vedere se il sito sembra piu vicino al tuo furgone, logo o divisa.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowColorTip(false)}
                      className="mt-3 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white"
                    >
                      Ok, cambio colori
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-4 grid gap-3 rounded-2xl bg-slate-50 p-3 text-xs text-slate-600 md:grid-cols-3">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                Colori e font possono cambiare molto di piu.
              </div>
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                Immagini, sezioni e testi sono sostituibili.
              </div>
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                Foto reali del team rendono il sito ancora piu forte.
              </div>
            </div>
          </div>
          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
            <div className="font-semibold text-slate-950">Quanto e personalizzabile?</div>
            <p className="mt-1">
              Molto piu di questa anteprima: possiamo cambiare struttura, foto, testi, servizi, zone, dominio,
              CTA e collegamento al backend Halomora in base alla tua impresa.
            </p>
            <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
              Suggerimento: prova nome e colori, poi naviga le pagine come se fosse gia il sito della tua impresa.
            </div>
          </div>
        </div>
      </section>

      {selectedStyle.variant === "local" && activePage === "home" && (
        <div className={selectedStyle.heroClass}>
          <header className="container mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
            <Link to="#" className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-md" style={{ background: "var(--demo-primary)" }}>
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xl font-bold leading-none" style={{ fontFamily: "var(--demo-heading-font)" }}>
                  {companyName || selectedStyle.company}
                </div>
                <div className="text-xs text-slate-500">Impresa di pulizie locale</div>
              </div>
            </Link>
            <nav className="hidden items-center gap-6 text-sm font-semibold text-slate-700 lg:flex">
              {navItems.map((item) => (
                <Link
                  key={item.page}
                  to={pagePath(item.page)}
                  className={activePage === item.page ? "text-slate-950" : "hover:text-slate-950"}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <Menu className="h-6 w-6 lg:hidden" />
          </header>

          <section className="container mx-auto grid max-w-7xl gap-10 px-6 pb-16 pt-8 md:pb-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div className="relative">
              <div className="mb-5 inline-flex rounded-full border border-sky-100 bg-white px-4 py-2 text-sm font-semibold text-sky-900 shadow-sm">
                {selectedStyle.tone}
              </div>
              <h1 className="max-w-3xl text-4xl font-bold leading-[1.05] tracking-tight text-slate-950 md:text-6xl" style={{ fontFamily: "var(--demo-heading-font)" }}>
                {selectedStyle.headline}
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">{selectedStyle.subheadline}</p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link to={pagePath("preventivo")} className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-bold text-white shadow-lg" style={{ background: "var(--demo-primary)" }}>
                  Richiedi preventivo gratuito
                  <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to={pagePath("servizi")} className="inline-flex h-12 items-center justify-center rounded-full border border-slate-200 bg-white px-7 text-sm font-bold text-slate-800 shadow-sm">
                  Scopri i servizi
                </Link>
              </div>
            </div>

            <div className="rounded-[2rem] border border-sky-100 bg-white p-5 shadow-md">
              <img
                src={selectedStyle.heroImage}
                alt={`Copertina ${companyName || selectedStyle.company}`}
                loading="eager"
                decoding="async"
                className="mb-4 h-48 w-full rounded-[1.5rem] object-cover"
              />
              <div className="rounded-[1.5rem] bg-[var(--demo-surface)] p-6">
                <div className="text-sm font-semibold text-slate-500">Richiesta rapida</div>
                <div className="mt-4 space-y-3">
                  <Input placeholder="Nome e cognome" />
                  <Input placeholder="Telefono" />
                  <Textarea rows={4} placeholder="Appartamento, ufficio o altro servizio?" />
                  <Button className="w-full rounded-full" style={{ background: "var(--demo-primary)" }}>Invia richiesta</Button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                {["Risposta 24h", "Personale assicurato", "Zone locali"].map((item) => (
                  <div key={item} className="rounded-2xl bg-slate-50 p-3 text-center text-slate-700">
                    <Star className="mx-auto mb-1 h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {selectedStyle.variant === "ops" && activePage === "home" && (
        <div className={selectedStyle.heroClass}>
          <header className="container mx-auto grid max-w-7xl grid-cols-[1fr_auto] items-center gap-4 px-6 py-5 lg:grid-cols-[0.75fr_1fr_auto]">
            <Link to="#" className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: "var(--demo-primary)" }}>
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-lg font-black uppercase tracking-tight" style={{ fontFamily: "var(--demo-heading-font)" }}>
                  {companyName || selectedStyle.company}
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-200">Operations ready</div>
              </div>
            </Link>
            <nav className="hidden justify-center gap-2 lg:flex">
              {navItems.map((item) => (
                <Link
                  key={item.page}
                  to={pagePath(item.page)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] ${
                    activePage === item.page
                      ? "border-white/30 bg-white/15 text-white"
                      : "border-white/10 text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link to={pagePath("preventivo")} className="hidden rounded-xl px-5 py-2.5 text-sm font-black uppercase text-white md:inline-flex" style={{ background: "var(--demo-primary)" }}>
              Nuova richiesta
            </Link>
          </header>

          <section className="container mx-auto grid max-w-7xl gap-8 px-6 py-12 md:py-18 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <Badge className="mb-5 border-emerald-300/20 bg-emerald-300/10 text-emerald-100 hover:bg-emerald-300/10">
                {selectedStyle.tone}
              </Badge>
              <h1 className="max-w-3xl text-4xl font-black uppercase leading-[0.98] tracking-[-0.04em] md:text-6xl" style={{ fontFamily: "var(--demo-heading-font)" }}>
                {selectedStyle.headline}
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">{selectedStyle.subheadline}</p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link to={pagePath("preventivo")} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 text-sm font-black uppercase text-white shadow-md" style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}>
                  Apri richiesta
                  <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to={pagePath("servizi")} className="inline-flex h-12 items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 text-sm font-bold text-white">
                  Vedi copertura
                </Link>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-slate-900/80 p-4 shadow-lg">
              <div className="mb-3 overflow-hidden rounded-xl border border-white/10">
                <img
                  src={selectedStyle.heroImage}
                  alt={`Team operativo ${companyName || selectedStyle.company}`}
                  loading="eager"
                  decoding="async"
                  className="h-44 w-full object-cover opacity-85"
                />
              </div>
              <div className="mb-3 grid grid-cols-3 gap-3">
                {["Lead oggi", "Zone attive", "Urgenze"].map((label, index) => (
                  <div key={label} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="text-2xl font-black text-white">{index === 0 ? "12" : index === 1 ? "6" : "3"}</div>
                    <div className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-white p-5 text-slate-950">
                <div className="mb-4 flex items-center justify-between">
                  <div className="font-black uppercase">Inbox preventivi</div>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Live</span>
                </div>
                {["Pulizia cambio - Jesolo", "Ufficio - San Dona", "Post-cantiere - Caorle"].map((item) => (
                  <div key={item} className="mb-2 flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm">
                    <span>{item}</span>
                    <Check className="h-4 w-4 text-emerald-600" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {selectedStyle.variant === "premium" && activePage === "home" && (
        <div className={selectedStyle.heroClass}>
          <header className="container mx-auto flex max-w-7xl items-center justify-between px-6 py-7">
            <Link to="#" className="text-center">
              <div className="text-xs uppercase tracking-[0.34em] text-fuchsia-200">Atelier pulizie</div>
              <div className="mt-1 text-2xl font-semibold tracking-wide" style={{ fontFamily: "var(--demo-heading-font)" }}>
                {companyName || selectedStyle.company}
              </div>
            </Link>
            <nav className="hidden items-center gap-7 text-sm text-slate-200 lg:flex">
              {navItems.map((item) => (
                <Link
                  key={item.page}
                  to={pagePath(item.page)}
                  className={activePage === item.page ? "text-white" : "hover:text-white"}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link to={pagePath("preventivo")} className="hidden rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white md:inline-flex">
              Concierge preventivi
            </Link>
          </header>

          <section className="container mx-auto max-w-7xl px-6 pb-16 pt-10 md:pb-24">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-5 text-sm uppercase tracking-[0.28em] text-fuchsia-200">{selectedStyle.tone}</div>
              <h1 className="text-5xl font-semibold leading-[1.02] tracking-[-0.03em] md:text-7xl" style={{ fontFamily: "var(--demo-heading-font)" }}>
                {selectedStyle.headline}
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">{selectedStyle.subheadline}</p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to={pagePath("preventivo")} className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-8 text-sm font-semibold text-white shadow-md" style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}>
                  Richiedi disponibilita
                  <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to={pagePath("servizi")} className="inline-flex h-12 items-center justify-center rounded-full border border-white/20 bg-white/5 px-8 text-sm font-semibold text-white">
                  Esperienza servizi
                </Link>
              </div>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-[0.8fr_1.4fr_0.8fr]">
              <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5">
                <img src={selectedStyle.serviceImages[0]} alt="Dettaglio servizio premium" loading="lazy" decoding="async" className="mb-4 h-32 w-full rounded-[1.25rem] object-cover" />
                <div className="text-3xl font-semibold" style={{ fontFamily: "var(--demo-heading-font)" }}>24h</div>
                <div className="text-sm text-slate-300">Risposta elegante e rapida</div>
              </div>
              <div className="rounded-[2rem] border border-white/10 bg-white p-6 text-slate-950 shadow-lg">
                <img src={selectedStyle.heroImage} alt={`Copertina ${companyName || selectedStyle.company}`} loading="eager" decoding="async" className="mb-5 h-48 w-full rounded-[1.5rem] object-cover" />
                <div className="text-sm uppercase tracking-[0.18em] text-violet-500">Booking preview</div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <Input placeholder="Property manager / Cliente" />
                  <Input placeholder="Data cambio ospite" />
                  <Textarea className="md:col-span-2" rows={4} placeholder="Standard richiesto, orario check-in, note" />
                  <Button className="md:col-span-2 rounded-full" style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}>Invia richiesta</Button>
                </div>
              </div>
              <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5">
                <img src={selectedStyle.serviceImages[1]} alt="Turnover hospitality" loading="lazy" decoding="async" className="mb-4 h-32 w-full rounded-[1.25rem] object-cover" />
                <div className="text-3xl font-semibold" style={{ fontFamily: "var(--demo-heading-font)" }}>STR</div>
                <div className="text-sm text-slate-300">Pensato per ospitalita e turnover</div>
              </div>
            </div>
          </section>
        </div>
      )}

      {activePage !== "home" && (
        <section className="border-b border-slate-200 bg-white">
          <div className="container mx-auto max-w-7xl px-6 py-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <Link to={demoBasePath} className="flex items-center gap-3">
                <div
                  className={
                    selectedStyle.variant === "ops"
                      ? "flex h-11 w-11 items-center justify-center rounded-xl text-white"
                      : selectedStyle.variant === "premium"
                        ? "flex h-11 w-11 items-center justify-center rounded-full text-white"
                        : "flex h-11 w-11 items-center justify-center rounded-2xl text-white"
                  }
                  style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}
                >
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-950" style={{ fontFamily: "var(--demo-heading-font)" }}>
                    {companyName || selectedStyle.company}
                  </div>
                  <div className="text-xs text-slate-500">Pagina {activeNavLabel}</div>
                </div>
              </Link>
              <nav className="flex flex-wrap gap-2">
                {navItems.map((item) => (
                  <Link
                    key={item.page}
                    to={pagePath(item.page)}
                    className={`rounded-full border px-4 py-2 text-xs font-bold ${
                      activePage === item.page
                        ? "border-transparent text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                    }`}
                    style={activePage === item.page ? { background: "var(--demo-primary)" } : undefined}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
              <div>
                <Badge variant="outline">{selectedStyle.name}</Badge>
                <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-950 md:text-5xl" style={{ fontFamily: "var(--demo-heading-font)" }}>
                  {activeNavLabel}
                </h1>
                <p className="mt-3 max-w-xl text-slate-600">
                  Questa e una pagina interna della demo. Puoi navigarla come un sito reale e tornare alla Home quando vuoi.
                </p>
              </div>
              <img
                src={selectedStyle.heroImage}
                alt={`${activeNavLabel} ${companyName || selectedStyle.company}`}
                loading="eager"
                decoding="async"
                className="h-56 w-full rounded-[1.75rem] object-cover shadow-md"
              />
            </div>
          </div>
        </section>
      )}

      <main>
        <section
          id="servizi"
          className={
            activePage !== "home" && activePage !== "servizi"
              ? "hidden"
              : selectedStyle.variant === "premium"
              ? "container mx-auto max-w-7xl px-6 py-20"
              : selectedStyle.variant === "ops"
                ? "container mx-auto max-w-7xl px-6 py-14"
                : "container mx-auto max-w-7xl px-6 py-16"
          }
        >
          <div
            className={
              selectedStyle.variant === "premium"
                ? "mx-auto mb-10 max-w-3xl text-center"
                : selectedStyle.variant === "ops"
                  ? "mb-8 flex max-w-5xl flex-col justify-between gap-5 border-b border-slate-200 pb-6 md:flex-row md:items-end"
                  : "mb-8 max-w-3xl"
            }
          >
            <Badge variant="outline">Servizi</Badge>
            <div>
              <h2
                className={
                  selectedStyle.variant === "ops"
                    ? "mt-3 text-3xl font-black uppercase tracking-[-0.04em] text-slate-950 md:text-5xl"
                    : selectedStyle.variant === "premium"
                      ? "mt-3 text-4xl font-semibold tracking-[-0.03em] text-slate-950 md:text-5xl"
                      : "mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl"
                }
                style={{ fontFamily: "var(--demo-heading-font)" }}
              >
                I servizi principali
              </h2>
              <p className="mt-3 max-w-2xl text-slate-600">
                La struttura riprende un sito reale come JService: servizi chiari, CTA semplice e form preventivo.
              </p>
            </div>
          </div>
          <div
            className={
              selectedStyle.variant === "premium"
                ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3"
                : selectedStyle.variant === "ops"
                  ? "grid gap-3 md:grid-cols-2"
                  : "grid gap-5 md:grid-cols-2 lg:grid-cols-3"
            }
          >
            {services.map(([title, text], index) => (
              <Card
                key={title}
                className={
                  selectedStyle.variant === "premium"
                    ? "rounded-[2.25rem] border-violet-100 bg-white shadow-md"
                    : selectedStyle.variant === "ops"
                      ? "rounded-xl border-slate-300 bg-slate-50 shadow-none hover:translate-y-0"
                      : "border-slate-200 bg-white"
                }
              >
                <CardContent
                  className={
                    selectedStyle.variant === "ops"
                      ? "grid grid-cols-[auto_1fr] gap-4 p-5"
                      : selectedStyle.variant === "premium"
                        ? "p-7 text-center"
                        : "p-6"
                  }
                >
                  <img
                    src={selectedStyle.serviceImages[index % selectedStyle.serviceImages.length]}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                    className={
                      selectedStyle.variant === "ops"
                        ? "col-span-2 h-36 w-full rounded-lg object-cover"
                        : selectedStyle.variant === "premium"
                          ? "mb-5 h-44 w-full rounded-[1.5rem] object-cover"
                          : "mb-4 h-40 w-full rounded-[1.25rem] object-cover"
                    }
                  />
                  <div
                    className={
                      selectedStyle.variant === "ops"
                        ? "inline-flex h-10 w-10 items-center justify-center rounded-lg text-white"
                        : selectedStyle.variant === "premium"
                          ? "mx-auto mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full text-white"
                          : "mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white"
                    }
                    style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}
                  >
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3
                      className={
                        selectedStyle.variant === "ops"
                          ? "text-lg font-black uppercase tracking-tight text-slate-950"
                          : "text-xl font-semibold text-slate-950"
                      }
                      style={selectedStyle.variant === "premium" ? { fontFamily: "var(--demo-heading-font)" } : undefined}
                    >
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section
          id="zone"
          className={
            activePage !== "home" && activePage !== "zone"
              ? "hidden"
              : selectedStyle.variant === "ops"
              ? "bg-slate-950 py-16 text-white"
              : selectedStyle.variant === "premium"
                ? "bg-[linear-gradient(180deg,#fff_0%,var(--demo-surface)_100%)] py-20"
                : "bg-[var(--demo-surface)] py-16"
          }
        >
          <div
            className={
              selectedStyle.variant === "premium"
                ? "container mx-auto max-w-7xl px-6 text-center"
                : "container mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center"
            }
          >
            <div>
              <Badge variant="outline">Zone servite</Badge>
              <h2
                className={
                  selectedStyle.variant === "ops"
                    ? "mt-3 text-3xl font-black uppercase tracking-[-0.04em] text-white md:text-5xl"
                    : selectedStyle.variant === "premium"
                      ? "mx-auto mt-3 max-w-2xl text-4xl font-semibold tracking-[-0.03em] text-slate-950 md:text-5xl"
                      : "mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl"
                }
                style={{ fontFamily: "var(--demo-heading-font)" }}
              >
                Copertura locale, comunicata bene.
              </h2>
              <p className={selectedStyle.variant === "ops" ? "mt-3 text-slate-300" : "mt-3 text-slate-600"}>
                Le zone possono essere gestite come contenuto del sito e come dato operativo nel backend.
              </p>
            </div>
            <div className={selectedStyle.variant === "premium" ? "mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-3" : "flex flex-wrap gap-3"}>
              {zones.map((zone) => (
                <span
                  key={zone}
                  className={
                    selectedStyle.variant === "ops"
                      ? "rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-slate-200"
                      : selectedStyle.variant === "premium"
                        ? "rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-lg"
                        : "rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
                  }
                >
                  {zone}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section id="perche" className={activePage === "home" ? "container mx-auto max-w-7xl px-6 py-16" : "hidden"}>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              [ShieldCheck, "Clienti tuoi", "Il sito parla con il tuo brand. Halomora resta infrastruttura dietro le quinte."],
              [CalendarCheck, "Richieste ordinate", "Ogni contatto arriva con servizio, zona e informazioni utili per rispondere meglio."],
              [Phone, "Meno caos", "Meno messaggi sparsi tra telefono, WhatsApp ed email: il flusso parte dal sito."],
            ].map(([Icon, title, text]) => (
              <Card key={String(title)}>
                <CardContent className="p-6">
                  <Icon className="mb-4 h-7 w-7" style={{ color: "var(--demo-primary)" }} />
                  <h3 className="text-xl font-semibold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section id="preventivo" className={activePage === "home" || activePage === "preventivo" ? "bg-slate-950 py-16 text-white" : "hidden"}>
          <div className="container mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <Badge className="mb-4 bg-white text-slate-950 hover:bg-white">Preventivo</Badge>
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Richiedi un preventivo gratuito</h2>
              <p className="mt-3 text-slate-300">
                Questa sezione puo inviare la richiesta al backend Halomora o al contatto commerciale dell'impresa.
              </p>
              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <div className="flex gap-3"><Mail className="h-4 w-4" /> info@azienda-demo.it</div>
                <div className="flex gap-3"><Phone className="h-4 w-4" /> +39 000 000 0000</div>
                <div className="flex gap-3"><MapPin className="h-4 w-4" /> Veneto e zone limitrofe</div>
              </div>
            </div>
            <Card className="bg-white">
              <CardContent className="grid gap-4 p-6 md:grid-cols-2">
                <Input placeholder="Nome" />
                <Input placeholder="Telefono" />
                <Input className="md:col-span-2" placeholder="Tipo di servizio" />
                <Textarea className="md:col-span-2" rows={5} placeholder="Messaggio" />
                <Button className="md:col-span-2" style={{ background: "linear-gradient(135deg,var(--demo-primary),var(--demo-secondary))" }}>
                  Invia richiesta
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="faq" className={activePage === "home" || activePage === "faq" ? "container mx-auto max-w-4xl px-6 py-16" : "hidden"}>
          <div className="mb-8 text-center">
            <Badge variant="outline">FAQ</Badge>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">Domande frequenti</h2>
          </div>
          <div className="space-y-3">
            {[
              ["Quanto costa?", "Il prezzo dipende da servizi, zone, contenuti e livello di integrazione con Halomora."],
              ["Posso usare il mio dominio?", "Si, il sito puo vivere su un dominio dell'impresa o su una struttura gestita."],
              ["Posso cambiare testi e colori?", "Si, questa demo mostra proprio la logica di personalizzazione iniziale."],
            ].map(([question, answer]) => (
              <details key={question} className="rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="cursor-pointer font-semibold text-slate-950">{question}</summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      {activePage !== "home" && !miniCustomizerDismissed ? (
        <div className="fixed bottom-5 right-5 z-50 w-[min(92vw,380px)]">
          {miniCustomizerOpen ? (
            <div className="rounded-[1.5rem] border border-sky-100 bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-950">
                    <Wand2 className="h-4 w-4" style={{ color: "var(--demo-primary)" }} />
                    Personalizza mentre navighi
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Anche sulle pagine interne puoi provare brand e colori. La versione finale puo cambiare molto di piu.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMiniCustomizerDismissed(true)}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200"
                >
                  Ok
                </button>
              </div>
              <div className="mt-4 grid gap-3">
                <Input value={companyName} onChange={(event) => setCompanyName(event.target.value)} placeholder="Nome azienda" />
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-semibold text-slate-600">
                    Primario
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(event) => setPrimaryColor(event.target.value)}
                      className="ml-auto h-7 w-9 cursor-pointer rounded border-0 bg-transparent"
                    />
                  </label>
                  <label className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-semibold text-slate-600">
                    Secondario
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={(event) => setSecondaryColor(event.target.value)}
                      className="ml-auto h-7 w-9 cursor-pointer rounded border-0 bg-transparent"
                    />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMiniCustomizerOpen(true);
                setShowPageTip(false);
              }}
              className="ml-auto flex items-center gap-3 rounded-full border border-sky-100 bg-sky-50 px-5 py-3 text-sm font-bold text-sky-950 shadow-md hover:bg-sky-100"
            >
              <Wand2 className="h-4 w-4" />
              Personalizza questa pagina
            </button>
          )}
          {showPageTip && !miniCustomizerOpen ? (
            <div className="mb-2 ml-auto w-fit rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-900 shadow-lg">
              Vuoi vedere questa pagina con il tuo brand? Apri il drawer.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
